import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { ShipmentTrackerStatus } from "@redotech/redo-model/fulfillments/outbound-shipment-label";
import { unaryRpc } from "@redotech/rpc/definition";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Statuses that represent a genuine inbound consolidation shipment a merchant
// should see. Everything omitted (Cancelled, Voided, Merged, Unmerged, Deleted,
// Invalid, OpenNotReceived, …) is internal plumbing or a downstream echo of an
// upstream order/return cancellation — never a real shipment arriving at the
// returns center. Owned here so the handler default and the dropdown options
// stay in lockstep.
export const InboundShipmentDisplayStatuses: readonly FulfillmentGroupStatus[] =
  [
    FulfillmentGroupStatus.Pending,
    FulfillmentGroupStatus.Open,
    FulfillmentGroupStatus.OnHold,
    FulfillmentGroupStatus.Scheduled,
    FulfillmentGroupStatus.ReadyForPickup,
    FulfillmentGroupStatus.ThirdPartyInProgress,
    FulfillmentGroupStatus.Closed,
  ];

const inboundShipmentLineItemSchema_ = z.object({
  title: z.string(),
  productTitle: z.string(),
  sku: z.string().nullable(),
  quantity: z.number(),
  returnHref: z.string().nullable(),
});
export const inboundShipmentLineItemSchema: z.ZodType<InboundShipmentLineItem> =
  inboundShipmentLineItemSchema_;
export interface InboundShipmentLineItem extends z.infer<
  typeof inboundShipmentLineItemSchema_
> {}

const inboundShipmentDestinationSchema_ = z.object({
  name: z.string(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  zip: z.string().nullable(),
  countryCode: z.string().nullable(),
});
export const inboundShipmentDestinationSchema: z.ZodType<InboundShipmentDestination> =
  inboundShipmentDestinationSchema_;
export interface InboundShipmentDestination extends z.infer<
  typeof inboundShipmentDestinationSchema_
> {}

const inboundShipmentOriginSchema_ = z.object({
  name: z.string(),
  city: z.string().nullable(),
  province: z.string().nullable(),
});
export const inboundShipmentOriginSchema: z.ZodType<InboundShipmentOrigin> =
  inboundShipmentOriginSchema_;
export interface InboundShipmentOrigin extends z.infer<
  typeof inboundShipmentOriginSchema_
> {}

const inboundShipmentSchema_ = z.object({
  id: z.string(),
  orderName: z.string(),
  status: createEnum(FulfillmentGroupStatus, {
    enumName: "FulfillmentGroupStatus",
  }),
  trackerStatus: createEnum(ShipmentTrackerStatus, {
    enumName: "ShipmentTrackerStatus",
  })
    .nullable()
    .optional(),
  carrier: z.string().nullable(),
  trackingCode: z.string().nullable(),
  trackingUrl: z.string().nullable(),
  itemCount: z.number(),
  lineItems: z.array(inboundShipmentLineItemSchema),
  origin: inboundShipmentOriginSchema,
  destination: inboundShipmentDestinationSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const inboundShipmentSchema: z.ZodType<InboundShipment> =
  inboundShipmentSchema_;
export interface InboundShipment extends z.infer<
  typeof inboundShipmentSchema_
> {}

export const listInboundConsolidatedShipments = unaryRpc({
  name: "listInboundConsolidatedShipments" as const,
  input: z.object({
    returnsCenterId: z.string().optional(),
    statuses: z
      .array(
        createEnum(FulfillmentGroupStatus, {
          enumName: "FulfillmentGroupStatus",
        }),
      )
      .optional(),
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    page: z.number().min(0).optional(),
    pageSize: z.number().min(1).max(200).default(25),
  }),
  output: z.object({
    items: z.array(inboundShipmentSchema),
    totalCount: z.number(),
    page: z.number(),
    pageSize: z.number(),
  }),
});

export type InboundShipmentsOutput = z.infer<
  (typeof listInboundConsolidatedShipments)["output"]
>;
